import { createReducer, on, Action } from '@ngrx/store';

import * as AuthActions from '../actions/auth.actions';
import * as storageHelper from '../../helpers/storage-helper';
import { LoggedInModuleType } from '../../../shared/interfaces/logged-in-module.type';
import { User } from '../../../shared/interfaces/user.type';

export enum LoginProcessState {
    NOT_STARTED,
    STARTED,
    LOGIN_IN_PROGRESS,
    FINISHED_SUCCESS,
    FINISHED_FAILED
};

export interface State {
    isAuthenticated: boolean;
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    loggedInModule: LoggedInModuleType | null;
    refreshTokenInProgress: boolean | null;
    errorMessage: string | null;
    loginProcessState: LoginProcessState;
    errorCode: string | null;
    twoFactorAuthToken: string | null;
}

// Hydrate the initial state from whichever storage currently holds the token
// (localStorage if "Remember Me" was on, sessionStorage if it wasn't). An
// expired access token is still kept here — the request interceptor will swap
// it for a fresh one on the next outbound call if the refresh token is still
// valid, instead of immediately punting the user back to /login.
const storedAccessToken = storageHelper.getItemWithExpiry('accessToken');
const storedRefreshToken = storageHelper.getItemWithExpiry('refreshToken');
const storedUser = storageHelper.getItemWithExpiry('user');
const hasUsableSession = !!storedUser && (!!storedAccessToken || !!storedRefreshToken);

export const initialState: State = {
    isAuthenticated: hasUsableSession,
    accessToken: storedAccessToken,
    refreshToken: storedRefreshToken,
    loggedInModule: storageHelper.getItemWithExpiry('loggedInModule'),
    refreshTokenInProgress: false,
    user: storedUser,
    errorMessage: null,
    loginProcessState: hasUsableSession ? LoginProcessState.FINISHED_SUCCESS : LoginProcessState.NOT_STARTED,
    errorCode: null,
    twoFactorAuthToken: null
};

const authReducer = createReducer(
    initialState,

    on(AuthActions.login, (state, { email, password }) => ({
        ...state,
        loginProcessState: LoginProcessState.STARTED
    })),

    on(AuthActions.twoFactorAuthRequired, (state, { user, twoFactorAuthToken }) => ({
        ...state,
        user,
        twoFactorAuthToken,
        loginProcessState: LoginProcessState.LOGIN_IN_PROGRESS
    })),

    on(AuthActions.loginSuccess, (state, { accessToken, refreshToken, user, loggedInModule }) => ({
        ...state,
        isAuthenticated: true,
        accessToken,
        refreshToken,
        loggedInModule,
        refreshTokenInProgress: false,
        user,
        loginProcessState: LoginProcessState.FINISHED_SUCCESS,
        errorMessage: null,
        errorCode: null
    })),

    on(AuthActions.refreshTokenInProgress, (state) => ({
        ...state,
        refreshTokenInProgress: true,
        errorMessage: null,
        errorCode: null
    })),

    on(AuthActions.refreshTokenSuccess, (state, { accessToken, refreshToken, user }) => ({
        ...state,
        isAuthenticated: true,
        accessToken,
        refreshToken,
        refreshTokenInProgress: false,
        user,
        errorMessage: null,
        errorCode: null
    })),

    on(AuthActions.loginFailure, (state, { error }) => {

        let errorMessage = 'Some Unknown Error Occurred.';
        let errorCode = 'Unknown Error';
        const httpError = error;
        console.warn('[AuthReducer] LOGIN_FAILURE:', httpError);

        if (httpError.status >= 400) {
            const errorFromServer = httpError.error;
            if (Array.isArray(errorFromServer) && errorFromServer.length > 0) {
                errorMessage = errorFromServer[0].message;
                errorCode = errorFromServer[0].code;
            }
        } else if (httpError.status < 100) {
            errorMessage = httpError.message;
            errorCode = httpError.statusText;
        }

        return {
            ...state,
            refreshTokenInProgress: false,
            errorMessage,
            errorCode,
            loginProcessState: LoginProcessState.FINISHED_FAILED,
        };
    }),

    on(AuthActions.logout, state => ({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        loggedInModule: null,
        refreshTokenInProgress: null,
        errorMessage: null,
        errorCode: null,
        loginProcessState: LoginProcessState.NOT_STARTED,
        twoFactorAuthToken: null
    })),


    on(AuthActions.unAuthorized, state => ({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        loggedInModule: null,
        refreshTokenInProgress: null,
        errorMessage: null,
        errorCode: null,
        loginProcessState: LoginProcessState.NOT_STARTED,
        twoFactorAuthToken: null
    })),


    on(AuthActions.updateUser, (state, { user }) => ({
        ...state,
        user,
    })),

    // Merge, not replace — /entrance/me returns roles/permissions, not the
    // full profile shape login does; a full replace would silently drop
    // fields (organisationName, orgDetails, etc.) already in the store.
    on(AuthActions.refreshPermissionsSuccess, (state, { user }) => ({
        ...state,
        user: state.user ? { ...state.user, ...user } : state.user,
    })),

    on(AuthActions.noAction, (state) => ({
        ...state
    }))

);

export function reducer(state: State | undefined, action: Action) {
    return authReducer(state, action);
}